import React, { useState, useEffect, useRef } from 'react';
import { Container, Button, Typography, Box, Paper, Alert, CircularProgress } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { authStyles } from '../common/AuthStyles';
import UserForm from '../user/UserForm';
import dayjs from 'dayjs';
import { useAuth } from '../../AuthContext';
import { logAnalyticsEvent } from '../../utils/analytics';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import MembershipPricingCards from '../subscription/MembershipPricingCards';
import { color, r } from '../../theme/tokens';

function RegistrationCompletion() {
  const { currentUser, loading: authLoading } = useAuth();
  const { getUserType } = useFeatureAccess();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    dietPreference: '',
    lifestage: {},
    password: '', // Not needed for completion
    confirmPassword: '' // Not needed for completion
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [isTrialSetupOnly, setIsTrialSetupOnly] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const hasInitializedRef = useRef(false);

  // Determine if this is a Google user
  const isGoogleUser = currentUser?.providerData?.some(p => p.providerId === 'google.com');
  const authMethod = isGoogleUser ? 'google' : 'email';

  useEffect(() => {
    // Pre-fill data from current user and check if returning
    const initializeUserData = async () => {
      if (authLoading) {
        return;
      }

      if (!currentUser) {
        navigate('/login');
        return;
      }

      const email = currentUser.email || '';
      let firstName = '';
      let lastName = '';
      let dateOfBirth = '';
      let gender = '';
      let dietPreference = '';
      let lifestage = {};
      
      // Check if user account was created more than 5 minutes ago (returning user)
      const userCreated = currentUser.metadata?.creationTime;
      if (userCreated) {
        const createdTime = new Date(userCreated);
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
        setIsReturningUser(createdTime < fiveMinutesAgo);
      }

      // Check if user already has complete profile data (never_subscribed users returning for trial setup)
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          firstName = userData.firstName || '';
          lastName = userData.lastName || '';
          dateOfBirth = userData.dateOfBirth || '';
          gender = userData.gender || '';
          dietPreference = userData.dietPreference || '';
          lifestage = userData.lifestage || {};

          // If user has complete profile and is never_subscribed, this is trial setup only
          const userType = getUserType();
          if (userType === 'never_subscribed' && firstName && lastName && dateOfBirth) {
            setIsTrialSetupOnly(true);
            if (searchParams.get('checkout_canceled') === 'true') {
              setShowPlanSelection(true);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
      
      if (isGoogleUser && !firstName) {
        // For Google users, parse the display name only if not already in database
        if (currentUser.displayName) {
          const nameParts = currentUser.displayName.split(' ');
          firstName = nameParts[0] || '';
          lastName = nameParts.slice(1).join(' ') || '';
        } else {
          // Fallback if Google doesn't provide displayName
          firstName = currentUser.email?.split('@')[0] || 'User';
          lastName = '';
        }
      }
      
      // Only initialize form data once to avoid clearing user input on re-renders
      if (!hasInitializedRef.current) {
        setFormData(prev => ({
          ...prev,
          email,
          firstName,
          lastName,
          dateOfBirth,
          gender,
          dietPreference,
          lifestage
        }));
        hasInitializedRef.current = true;
      }
    };

    initializeUserData();
  }, [currentUser, authLoading, navigate, isGoogleUser, getUserType, searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleComplete = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      setMessage('User not found. Please sign in again.');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      // If this is trial setup only, skip profile update and go straight to Stripe
      if (isTrialSetupOnly) {
        setShowPlanSelection(true);
        return;
      }

      // Normal profile completion flow
      const { firstName, lastName, dateOfBirth, gender, dietPreference, lifestage } = formData;

      // Age validation - check if birth date is at least 18 years ago
      if (dateOfBirth) {
        const birthDate = dayjs(dateOfBirth);
        const eighteenYearsAgo = dayjs().subtract(18, 'year');
        
        if (birthDate.isAfter(eighteenYearsAgo)) {
          setMessage('You must be at least 18 years old to use myStack.');
          setLoading(false);
          return;
        }
      }

      // Update user document with additional information
      const updateData = {
        firstName: firstName || '',
        lastName: lastName || '',
        dateOfBirth: dateOfBirth || '',
        gender: gender || '',
        dietPreference: dietPreference || '',
        lifestage: lifestage || {},
        registrationCompleted: true,
        registrationCompletedAt: new Date().toISOString()
      };

      // Clear the completion flag for both user types
      if (isGoogleUser) {
        updateData.needsRegistrationCompletion = false;
      } else {
        updateData.needsProfileCompletion = false;
      }

      await updateDoc(doc(db, 'users', currentUser.uid), updateData);

      // Log successful registration completion
      logAnalyticsEvent('registration_completed', { 
        method: authMethod, 
        status: 'success',
        has_dob: !!dateOfBirth,
        has_name: !!(firstName && lastName)
      });

      setMessage('');
      setShowPlanSelection(true);
      
    } catch (error) {
      console.error('Registration completion error:', error);
      setMessage(isTrialSetupOnly ? 'Unable to proceed. Please try again.' : 'Failed to complete registration. Please try again.');
      logAnalyticsEvent('registration_completed', { 
        method: isTrialSetupOnly ? 'trial_setup' : authMethod, 
        status: 'failure', 
        error_message: error.message 
      });
    } finally {
      setLoading(false);
    }
  };

  // Validation - require firstName and DOB
  // lastName is optional for Google users (since Google may not provide it)
  const isValid = isTrialSetupOnly || (
    formData.firstName && 
    formData.dateOfBirth &&
    (isGoogleUser || formData.lastName) // lastName required for email users, optional for Google
  );

  // Dynamic messaging based on user type
  const getPageTitle = () => {
    if (isTrialSetupOnly) {
      return 'Start Your Membership';
    }
    if (showPlanSelection) {
      return 'Choose Your Membership';
    }
    return isGoogleUser ? 'Complete Your Registration' : 'Personalize Your Experience';
  };

  const getPageDescription = () => {
    if (isTrialSetupOnly) {
      return 'Choose a plan to activate your 14-day free trial with secure Stripe checkout. You won\'t be charged today.';
    }
    if (showPlanSelection) {
      return 'Your profile is saved. Choose a plan to activate your 14-day free trial.';
    }
    return 'These few details are needed to create your science-based supplement stacks.';
  };

  if (authLoading) {
    return (
      <Container component="main" maxWidth="sm" sx={{ pb: { xs: 10, sm: 0 } }}>
        <Paper
          elevation={0}
          sx={{
            ...authStyles.paper,
            mt: { xs: 3, sm: authStyles.paper.marginTop },
            textAlign: 'center',
          }}
        >
          <CircularProgress size={28} />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            Restoring your session...
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container
      component="main"
      maxWidth={(showPlanSelection || isTrialSetupOnly) ? 'md' : 'sm'}
      sx={{ pb: { xs: 10, sm: 0 } }}
    >
      <Paper
        elevation={0}
        sx={{
          ...authStyles.paper,
          mt: { xs: 3, sm: authStyles.paper.marginTop },
          px: { xs: 3, sm: 4 },
        }}
      >
        <Typography component="h1" variant="h4" sx={authStyles.title}>
          {getPageTitle()}
        </Typography>

        {/* Step indicator with progress dots */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          mb: 2,
          mt: 1
        }}>
          {/* Progress dots */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mb: 1
          }}>
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: color.accent,
              mr: 1
            }} />
            <Box sx={{
              width: 16,
              height: 2,
              backgroundColor: color.accent,
              mr: 1
            }} />
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: color.accent,
              mr: 1
            }} />
            <Box sx={{
              width: 16,
              height: 2,
              backgroundColor: color.line,
              mr: 1
            }} />
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: color.line
            }} />
          </Box>
          
          <Typography variant="body2" sx={{ 
            color: 'text.secondary',
            fontSize: '0.875rem',
            fontWeight: 500
          }}>
            Step 2 of 3
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', textAlign: 'center' }}>
          {getPageDescription()}
        </Typography>

        {message && (
          <>
            {message.includes('One more step') ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  mb: 3,
                  p: 2,
                  backgroundColor: color.mint,
                  borderRadius: 1
                }}
              >
                <CircularProgress size={20} sx={{ color: color.accent }} />
                <Typography variant="body2" sx={{ color: color.evergreen, fontWeight: 500, fontSize: '0.875rem' }}>
                  {message}
                </Typography>
              </Box>
            ) : (
              <Alert 
                severity={message.includes('completed') || message.includes('Redirecting') ? 'success' : 'error'} 
                sx={{ mb: 3 }}
              >
                {message}
              </Alert>
            )}
          </>
        )}

        {(showPlanSelection || isTrialSetupOnly) ? (
          <MembershipPricingCards
            source={isTrialSetupOnly ? 'registration_trial_setup_only' : 'registration_completion'}
            compact
            title="Choose Your Membership"
            description="Choose a plan to activate your 14-day free trial."
            showHeader={false}
            cancelPath="/complete-registration?checkout_canceled=true"
          />
        ) : (
        <Box component="form" onSubmit={handleComplete} noValidate>
          {!isTrialSetupOnly && (
            <UserForm
              formData={formData}
              handleChange={handleChange}
              showPassword={false} // Don't show password fields in completion
              showEmail={false} // Don't show email in completion
              nameReadOnly={isGoogleUser} // Google names are pre-filled and read-only
              showDietPreference={false}
            />
          )}

          <Button
            type="submit"
            fullWidth
            disabled={!isValid || loading}
            sx={{
              ...authStyles.secondaryButton,
              border: `${r(2)} solid`,
              borderColor: isTrialSetupOnly ? color.accent : 'primary.main',
              color: isTrialSetupOnly ? color.evergreen : 'primary.main',
              fontWeight: 600,
              mt: 3,
              '&:hover': {
                backgroundColor: isTrialSetupOnly ? color.accent : 'primary.main',
                // `accentInk` on the accent fill — white measures 4.02:1 there,
                // under the 4.5:1 body-text floor (brief §9; the same correction
                // PR 280 made). The `primary.main` branch fills with evergreen,
                // where white is fine.
                color: isTrialSetupOnly ? color.accentInk : 'white',
                border: `${r(2)} solid`,
                borderColor: isTrialSetupOnly ? color.accent : 'primary.main',
              },
              '&:disabled': {
                borderColor: 'action.disabled',
                color: 'action.disabled'
              }
            }}
          >
            {loading
              ? (isTrialSetupOnly ? 'Starting Trial...' : 'Saving Profile...')
              : (isTrialSetupOnly ? 'Start Free Trial' : 'Continue')
            }
          </Button>

        </Box>
        )}
      </Paper>
    </Container>
  );
}

export default RegistrationCompletion;
